import { useEffect, useLayoutEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import type { HtmlElementProps } from '@/types';
import { useToggle } from '@/hooks/useToggle';
import { useString } from '@/hooks/useString';
import Tooltip, { type TooltipProps } from '@/components/Tooltip';
import { classes, getLabel } from './utils';

export interface TruncatedTextProps extends HtmlElementProps {
  arrow?: TooltipProps['arrow'];
  className?: string;
  textClassName?: string;
  maxLength?: number;
  tooltipPlacement?: TooltipProps['placement'];
  children: string;
}

export default function TruncatedText({
  arrow = false,
  className,
  textClassName,
  maxLength,
  tooltipPlacement,
  children,
  ...props
}: TruncatedTextProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [label, setLabel] = useString();
  const [isTruncated, toggleTruncated] = useToggle();

  useEffect(() => {
    const labelSettings = getLabel(children, maxLength);
    setLabel(labelSettings.label);
    toggleTruncated(labelSettings.isTruncated);
  }, [maxLength, children]);

  useLayoutEffect(() => {
    if (maxLength === undefined) checkTruncate();
  }, [label]);

  const checkTruncate = () => {
    if (!contentRef.current) return;
    const { offsetWidth, scrollWidth } = contentRef.current;
    if (offsetWidth < scrollWidth) {
      setLabel(children);
      toggleTruncated(true);
    }
  };

  const content = (
    <div
      ref={contentRef}
      className={twMerge(
        maxLength === undefined && classes.clipText,
        textClassName
      )}
      {...props}>
      {label}
    </div>
  );

  return (
    <div className={twMerge(classes.wrapper, className)} {...props}>
      {isTruncated ? (
        <Tooltip arrow={arrow} title={children} placement={tooltipPlacement}>
          {content}
        </Tooltip>
      ) : (
        content
      )}
    </div>
  );
}
